import React, { useEffect, useState, useCallback, useRef } from 'react';
import { cn } from '../lib/utils';
import { ConversationList } from '../components/ConversationList';
import { ChatWindow } from '../components/ChatWindow';
import { CustomerPanel } from '../components/CustomerPanel';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertCircle, WifiOff } from 'lucide-react';
import {
  getConversations,
  createConversation,
  getMessages,
  sendMessage as apiSendMessage,
  updateConversation as apiUpdateConversation,
  closeConversation as apiCloseConversation,
  reopenConversation as apiReopenConversation,
  deleteConversation as apiDeleteConversation,
  assignConversation as apiAssignConversation,
  unassignConversation as apiUnassignConversation,
  addNote as apiAddNote,
  markAsRead,
  getQuickReplies,
  getAttendants,
  transferConversation as apiTransferConversation,
  acceptConversation as apiAcceptConversation,
  getSystemStatus,
  getAdminSettings
} from '../lib/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { useSound } from '../hooks/useSound';
import { toast } from 'sonner';

export const Conversations = ({ currentUser, initialSelectedId, isMobile }) => {
  const [conversations, setConversations] = useState([]);
  const [closedConversations, setClosedConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastInitialId, setLastInitialId] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [deleteConversationsEnabled, setDeleteConversationsEnabled] = useState(true);
  const [promessaDias, setPromessaDias] = useState(5);
  const [activeMobileTab, setActiveMobileTab] = useState('chat'); // 'chat' or 'info'

  const { playNotificationSound } = useSound();

  // Use ref to avoid re-creating WebSocket handler on every selection change
  const selectedConversationRef = useRef(selectedConversation);
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  const upsertConversation = useCallback((list, conversation) => {
    if (!conversation?.id) return list;
    // Always remove existing instance and add to top
    const filtered = list.filter(c => c.id !== conversation.id);
    return [conversation, ...filtered];
  }, []);

  const upsertClosedConversation = useCallback((list, conversation, limit = 10) => {
    if (!conversation?.id) return list.slice(0, limit);
    const filtered = list.filter(c => c.id !== conversation.id);
    return [conversation, ...filtered].slice(0, limit);
  }, []);

  // Fetch system status
  const fetchSystemStatus = useCallback(async () => {
    try {
      const response = await getSystemStatus();
      setSystemStatus(response.data);
      setChatEnabled(response.data.chat_enabled);
    } catch (error) {
      console.error('Error fetching system status:', error);
    }
  }, []);

  // Check if conversation data actually changed in meaningful fields
  const hasConversationChanged = useCallback((prev, next) => {
    if (!prev || !next) return true;
    return prev.status !== next.status ||
      prev.assigned_to !== next.assigned_to ||
      prev.assigned_name !== next.assigned_name ||
      prev.customer_name !== next.customer_name ||
      prev.unread_count !== next.unread_count ||
      prev.last_message !== next.last_message;
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log('WebSocket message:', data);

    const canSeeConversation = (conv) => {
      if (!conv) return false;
      // Admins and supervisors can see everything
      if (currentUser?.role === 'admin' || currentUser?.role === 'supervisor') return true;
      // For attendants, check if they are the assignee or if it's unassigned
      return !conv.assigned_to || conv.assigned_to === currentUser?.id;
    };

    // Use ref to avoid dependency on selectedConversation state
    const currentSelected = selectedConversationRef.current;

    switch (data.type) {
      case 'new_message':
        // Play sound for new customer messages
        if (data.message.sender_type === 'customer') {
          playNotificationSound();
        }

        // Update messages if viewing the same conversation
        if (data.conversation_id === currentSelected?.id) {
          setMessages(prev => {
            if (prev.some(m => m.id === data.message.id)) {
              return prev;
            }
            return [...prev, data.message];
          });
        }

        // Update conversation in list
        if (data.conversation) {
          if (canSeeConversation(data.conversation)) {
            setConversations(prev => upsertConversation(prev, data.conversation));
          }
        }
        break;

      case 'message_status':
        setMessages(prev =>
          prev.map(m => m.id === data.message_id ? { ...m, status: data.status } : m)
        );
        break;

      case 'conversation_updated':
      case 'conversation_transferred':
      case 'conversation_accepted':
      case 'conversation_opened':
      case 'conversation_assigned':
      case 'conversation_unassigned':
        // Check if user can still see this conversation
        if (data.conversation.status === 'closed') {
          // Move to closed list
          setConversations(prev => prev.filter(c => c.id !== data.conversation.id));
          setClosedConversations(prev => upsertClosedConversation(prev, data.conversation));
          // Update selected only if data actually changed
          if (currentSelected?.id === data.conversation.id) {
            if (hasConversationChanged(currentSelected, data.conversation)) {
              setSelectedConversation(data.conversation);
            }
          }
        } else if (canSeeConversation(data.conversation)) {
          // User can see it - update or add
          setConversations(prev => upsertConversation(prev, data.conversation));
          // Only update selected if data actually changed
          if (currentSelected?.id === data.conversation.id) {
            if (hasConversationChanged(currentSelected, data.conversation)) {
              setSelectedConversation(data.conversation);
            }
          }
        } else {
          // User can no longer see it - remove from list
          setConversations(prev => prev.filter(c => c.id !== data.conversation.id));
          // Clear selection if it was selected
          if (currentSelected?.id === data.conversation.id) {
            setSelectedConversation(null);
            setMessages([]);
            toast.info('Conversa transferida para outro atendente');
          }
        }

        // Notify if transferred or assigned to current user
        if (data.type === 'conversation_transferred' && data.conversation.assigned_to === currentUser?.id) {
          playNotificationSound();
          toast.info('Você recebeu uma nova conversa!');
        }
        if (data.type === 'conversation_assigned' && data.conversation.assigned_to === currentUser?.id) {
          toast.success('Conversa atribuída a você');
        }
        break;

      case 'conversation_closed':
        setConversations(prev => prev.filter(c => c.id !== data.conversation.id));
        setClosedConversations(prev => upsertClosedConversation(prev, data.conversation));
        if (currentSelected?.id === data.conversation.id) {
          if (hasConversationChanged(currentSelected, data.conversation)) {
            setSelectedConversation(data.conversation);
          }
        }
        break;

      case 'conversation_deleted':
        // Remove from both lists
        setConversations(prev => prev.filter(c => c.id !== data.conversation_id));
        setClosedConversations(prev => prev.filter(c => c.id !== data.conversation_id));
        // Clear selection if it was the deleted conversation
        if (currentSelected?.id === data.conversation_id) {
          setSelectedConversation(null);
          setMessages([]);
        }
        break;

      case 'conversation_reopened':
        // Remove from closed
        setClosedConversations(prev => prev.filter(c => c.id !== data.conversation.id));
        // Add to active if can see
        if (canSeeConversation(data.conversation)) {
          setConversations(prev => upsertConversation(prev, data.conversation));
        }
        break;

      case 'conversation_created':
        if (canSeeConversation(data.conversation)) {
          playNotificationSound();
          setConversations(prev => upsertConversation(prev, data.conversation));
          toast.info('Nova conversa iniciada!');
        }
        break;

      default:
        break;
    }
  }, [playNotificationSound, currentUser, upsertConversation, upsertClosedConversation, hasConversationChanged]);

  const { isConnected } = useWebSocket(handleWebSocketMessage);

  // Fetch initial data
  const fetchData = async () => {
    try {
      const [convRes, closedRes, qrRes, attRes, adminSettingsRes] = await Promise.all([
        getConversations(),
        getConversations({ status: 'closed' }),
        getQuickReplies(),
        getAttendants(),
        getAdminSettings()
      ]);
      setConversations(convRes.data);
      setClosedConversations(closedRes.data);
      setQuickReplies(qrRes.data);
      setAttendants(attRes.data);
      setDeleteConversationsEnabled(adminSettingsRes?.data?.delete_conversations_enabled !== false);
      setPromessaDias(adminSettingsRes?.data?.promessa_dias || 5);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSystemStatus();

    // Check system status periodically
    const statusInterval = setInterval(fetchSystemStatus, 30000);
    return () => clearInterval(statusInterval);
  }, [fetchSystemStatus]);

  // Handle initial selected conversation from Dashboard
  useEffect(() => {
    // Only process if we have a new initialSelectedId and data is loaded
    if (!initialSelectedId || initialSelectedId === lastInitialId || loading) {
      return;
    }

    console.log('Conversations: processing initialSelectedId', initialSelectedId);
    setLastInitialId(initialSelectedId);

    // First try to find in active conversations
    let conv = conversations.find(c => c.id === initialSelectedId);
    console.log('Found in active:', !!conv);

    // If not found, try closed conversations
    if (!conv && closedConversations.length > 0) {
      conv = closedConversations.find(c => c.id === initialSelectedId);
      console.log('Found in closed:', !!conv);
      if (conv) {
        // Switch to closed filter
        setFilter('closed');
      }
    }

    // If still not found, fetch all including closed to find it
    if (!conv) {
      console.log('Not found, fetching closed...');
      getConversations({ status: 'closed' }).then(res => {
        const closedConv = res.data?.find(c => c.id === initialSelectedId);
        if (closedConv) {
          console.log('Found in fetched closed');
          setClosedConversations(res.data);
          setFilter('closed');
          handleSelectConversation(closedConv);
        } else {
          // Maybe it's an active conversation, try to select from what we have
          const activeConv = conversations.find(c => c.id === initialSelectedId);
          if (activeConv) {
            handleSelectConversation(activeConv);
          }
        }
      }).catch(console.error);
      return;
    }

    if (conv) {
      console.log('Selecting conversation');
      handleSelectConversation(conv);
    }
  }, [initialSelectedId, conversations, closedConversations, lastInitialId, loading]);

  // Handle filter change - refetch closed if needed
  const handleFilterChange = async (newFilter) => {
    setFilter(newFilter);
    if (newFilter === 'closed') {
      try {
        const res = await getConversations({ status: 'closed' });
        setClosedConversations(res.data);
      } catch (error) {
        console.error('Error fetching closed conversations:', error);
      }
    }
  };

  // Select and accept conversation
  const handleSelectConversation = async (conversation) => {
    // First set the conversation to show loading state
    setSelectedConversation(conversation);
    if (isMobile) {
      setActiveMobileTab('chat');
    }

    try {
      // Load messages first
      const messagesResponse = await getMessages(conversation.id);
      setMessages(messagesResponse.data);

      // Then mark as read
      if (conversation.unread_count > 0) {
        await markAsRead(conversation.id);
        setConversations(prev =>
          prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c)
        );
      }

      // Auto-accept/assign only if needed (bot or waiting status)
      if (conversation.status === 'bot' || conversation.status === 'waiting') {
        if (!conversation.assigned_to || conversation.assigned_to === currentUser?.id) {
          // Don't await - let it happen in background
          handleAcceptConversation(conversation).catch(console.error);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erro ao carregar mensagens');
    }
  };

  const normalizePhone = (phone) => {
    if (!phone) return '';
    return String(phone).replace(/\D/g, '');
  };

  const handleOpenContact = async ({ name, phone }) => {
    const targetPhone = normalizePhone(phone);
    if (!targetPhone) {
      toast.error('Contato sem número');
      return;
    }

    // Try to find an existing conversation (active or closed)
    const existing =
      conversations.find(c => normalizePhone(c.customer_phone) === targetPhone) ||
      closedConversations.find(c => normalizePhone(c.customer_phone) === targetPhone);
    if (existing) {
      if (existing.status === 'closed') setFilter('closed');
      handleSelectConversation(existing);
      return;
    }

    try {
      const instanceId = selectedConversation?.instance_id || 'default';
      const res = await createConversation({
        instance_id: instanceId,
        customer_phone: targetPhone,
        customer_name: name || targetPhone
      });
      // Put it in list immediately and select
      setConversations(prev => {
        if (prev.some(c => c.id === res.data.id)) return prev;
        return [res.data, ...prev];
      });
      setFilter('all');
      handleSelectConversation(res.data);
    } catch (error) {
      console.error('Error creating conversation from contact:', error);
      toast.error('Erro ao abrir conversa do contato');
    }
  };

  // Accept conversation
  const handleAcceptConversation = async (conversation) => {
    try {
      const response = await apiAcceptConversation(conversation.id);
      setSelectedConversation(response.data);
      setConversations(prev =>
        prev.map(c => c.id === conversation.id ? response.data : c)
      );
      if (conversation.status === 'waiting' && conversation.assigned_to === currentUser?.id) {
        toast.success('Conversa aceita!');
      }
    } catch (error) {
      console.error('Error accepting conversation:', error);
      if (error.response?.status === 403) {
        toast.error('Conversa já atribuída a outro atendente');
        // Refresh conversations
        fetchData();
      }
    }
  };

  // Send message
  const handleSendMessage = async (content, contentType = 'text', mediaUrl = null) => {
    if (!selectedConversation) return;

    try {
      await apiSendMessage({
        conversation_id: selectedConversation.id,
        content,
        content_type: contentType,
        media_url: mediaUrl,
        sender_type: 'attendant',
        sender_id: currentUser?.id,
        sender_name: currentUser?.name || 'Atendente'
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  // Update conversation
  const handleUpdateConversation = async (data, options = {}) => {
    if (!selectedConversation) return;
    const { silent = false } = options;

    try {
      const response = await apiUpdateConversation(selectedConversation.id, data);
      setSelectedConversation(response.data);
      setConversations(prev =>
        prev.map(c => c.id === selectedConversation.id ? response.data : c)
      );
      if (!silent) {
        toast.success('Conversa atualizada');
      }
    } catch (error) {
      console.error('Error updating conversation:', error);
      toast.error('Erro ao atualizar conversa');
    }
  };

  // Transfer conversation
  const handleTransferConversation = async (attendant) => {
    if (!selectedConversation || !attendant) return;

    const { id: attendantId, name: attendantName } = attendant;

    try {
      await apiTransferConversation(selectedConversation.id, attendantId, attendantName);
      toast.success(`Conversa transferida para ${attendantName}`);
      // Clear selection as it's no longer ours
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Error transferring conversation:', error);
      toast.error('Erro ao transferir conversa');
    }
  };

  // Close conversation
  const handleCloseConversation = async () => {
    if (!selectedConversation) return;

    try {
      await apiCloseConversation(selectedConversation.id);
      toast.success('Conversa encerrada');
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Error closing conversation:', error);
      toast.error('Erro ao encerrar conversa');
    }
  };

  // Reopen conversation
  const handleReopenConversation = async () => {
    if (!selectedConversation) return;

    try {
      await apiReopenConversation(selectedConversation.id);
      toast.success('Conversa reaberta');
    } catch (error) {
      console.error('Error reopening conversation:', error);
      toast.error('Erro ao reabrir conversa');
    }
  };

  // Delete conversation
  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;

    try {
      await apiDeleteConversation(selectedConversation.id);
      toast.success('Conversa excluída');
      // Remove from lists
      setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      setClosedConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      // Clear selection
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir conversa');
    }
  };

  // Assign conversation to current user
  const handleAssignConversation = async () => {
    if (!selectedConversation) return;

    try {
      await apiAssignConversation(selectedConversation.id);
      toast.success('Conversa atribuída a você');
    } catch (error) {
      console.error('Error assigning conversation:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atribuir conversa');
    }
  };

  // Unassign conversation
  const handleUnassignConversation = async () => {
    if (!selectedConversation) return;

    try {
      await apiUnassignConversation(selectedConversation.id);
      toast.success('Atribuição removida');
    } catch (error) {
      console.error('Error unassigning conversation:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover atribuição');
    }
  };

  // Add note
  const handleAddNote = async (note) => {
    if (!selectedConversation) return;

    try {
      const response = await apiAddNote(selectedConversation.id, note);
      setSelectedConversation(response.data);
      toast.success('Nota adicionada');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Erro ao adicionar nota');
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="conversations-page">
      {/* System Status Alert */}
      {systemStatus && !chatEnabled && (
        <Alert variant="destructive" className="m-2 mb-0">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Chat Desabilitado</AlertTitle>
          <AlertDescription>
            {systemStatus.overall_message}
            {systemStatus.webhook?.status !== 'online' && systemStatus.webhook?.status !== 'unknown' && (
              <span className="block text-sm">Webhook: {systemStatus.webhook?.error}</span>
            )}
            {systemStatus.instances?.connected === 0 && (
              <span className="block text-sm">Nenhuma instância WhatsApp conectada.</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex-1 flex overflow-hidden relative">
        {/* WebSocket Status */}
        <div className={`absolute top-2 right-2 w-2 h-2 rounded-full z-50 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
          title={isConnected ? 'Conectado' : 'Desconectado'} />

        {/* Conversation List - Hidden on mobile if a conversation is selected */}
        {(!isMobile || !selectedConversation) && (
          <ConversationList
            conversations={conversations}
            closedConversations={closedConversations}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            filter={filter}
            onFilterChange={handleFilterChange}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            className={isMobile ? "w-full" : ""}
          />
        )}

        {/* Detail View Container (Chat or Info) */}
        {(!isMobile || selectedConversation) && (
          <div className={cn("flex-1 flex flex-col overflow-hidden", isMobile ? "w-full" : "")}>
            {/* Mobile Tab Toggle */}
            {isMobile && selectedConversation && (
              <div className="flex bg-white border-b border-slate-200 shrink-0">
                <button
                  onClick={() => setActiveMobileTab('chat')}
                  className={cn(
                    "flex-1 py-3 text-sm font-medium border-b-2 transition-colors",
                    activeMobileTab === 'chat'
                      ? "text-primary border-primary bg-primary/5"
                      : "text-slate-500 border-transparent hover:text-slate-700"
                  )}
                >
                  Mensagens
                </button>
                <button
                  onClick={() => setActiveMobileTab('info')}
                  className={cn(
                    "flex-1 py-3 text-sm font-medium border-b-2 transition-colors",
                    activeMobileTab === 'info'
                      ? "text-primary border-primary bg-primary/5"
                      : "text-slate-500 border-transparent hover:text-slate-700"
                  )}
                >
                  Dados
                </button>
              </div>
            )}

            <div className="flex-1 flex overflow-hidden relative">
              {/* Chat Window - Only shown if active tab is chat (or not mobile) */}
              {(!isMobile || activeMobileTab === 'chat') && (
                <ChatWindow
                  conversation={selectedConversation}
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  quickReplies={quickReplies}
                  onTransfer={handleTransferConversation}
                  onClose={handleCloseConversation}
                  onReopen={handleReopenConversation}
                  onDelete={handleDeleteConversation}
                  deleteEnabled={deleteConversationsEnabled}
                  onAssign={handleAssignConversation}
                  onUnassign={handleUnassignConversation}
                  onOpenContact={handleOpenContact}
                  attendants={attendants}
                  currentUser={currentUser}
                  chatDisabled={!chatEnabled}
                  chatDisabledReason={systemStatus?.overall_message}
                  isMobile={isMobile}
                  onBack={() => setSelectedConversation(null)}
                />
              )}

              {/* Customer Panel - Shown on desktop OR if active tab is info on mobile */}
              {(!isMobile || activeMobileTab === 'info') && (
                <CustomerPanel
                  conversation={selectedConversation}
                  onUpdateConversation={handleUpdateConversation}
                  onAddNote={handleAddNote}
                  promessaDias={promessaDias}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
